import { Row } from '@tanstack/react-table'
import { actionColumn, renderHeaderTooltip } from '@utils/assessmentReportUtils'
import { formatReportDate } from '@utils/dateUtils'

import {
  Database_Procs_JTFAggregatedByLevel_Item,
  Database_Procs_TeamJTF_Item
} from '@suntex.fim/api-client'
import { ColumnType, Tooltip } from '@suntex.fim/ui'

import { generateScrollableAssessmentCommonColumns } from './CommonColumns'

const operations = {
  Addition: 'add',
  Subtraction: 'sub',
  Multiplication: 'mul',
  Division: 'div'
}

function getArithmeticKey(operation: string) {
  return operations[operation as keyof typeof operations]
}

/**
 * handles hidable columns list
 */

export const hidableSelectLabel: Record<string, string> = {
  userId: 'User ID',
  displayName: 'Display Name',
  stickers: 'Stickers',
  idNumber: 'Id',
  Addition: 'Addition',
  Subtraction: 'Subtraction',
  Multiplication: 'Multiplication',
  Division: 'Division',
  add_percent_pre: 'Pre',
  add_percent_post: 'Post',
  add_percent_best: 'Best',
  sub_percent_pre: 'Pre',
  sub_percent_post: 'Post',
  sub_percent_best: 'Best',
  mul_percent_pre: 'Pre',
  mul_percent_post: 'Post',
  mul_percent_best: 'Best',
  div_percent_pre: 'Pre',
  div_percent_post: 'Post',
  div_percent_best: 'Best'
}

export const schoolHidableSelectLabel: Record<string, string> = {
  name: 'Name',
  desc: 'Class Name',
  contactName: "Teacher's Name",
  activeStudents: 'Active Students',
  numberPretest: '# Taking Test Pre',
  numberFluentPretest: '# Proficient+ Pre',
  preProficient: '% Proficient+ Pre',
  numberPosttest: '# Taking Test Post',
  numberFluent: '# Proficient+ Post',
  postProficient: '% Proficient+ Post'
}

const subHeaders = ['pre', 'post', 'best']
/**
 * returns JSX for just the fact report table
 */
function columns({
  sendMessage
}: {
  sendMessage: (t: Row<Database_Procs_TeamJTF_Item>[]) => void
}) {
  const jtfColumns: ColumnType<Database_Procs_TeamJTF_Item>[] = [
    'Addition',
    'Subtraction',
    'Multiplication',
    'Division'
  ].map((key) => ({
    header: key,
    id: key,
    columns: subHeaders.map((subKey) => {
      const id = `${getArithmeticKey(key)}_percent_${subKey}`
      const dateKey = `${getArithmeticKey(
        key
      )}_${subKey}_date` as keyof Database_Procs_TeamJTF_Item
      return {
        header: () => (
          <span style={{ whiteSpace: 'nowrap' }}>{hidableSelectLabel[id]}</span>
        ),
        id,
        accessorKey: id,
        cell: (info) => {
          const allValue = info.row.original
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const best = info.getValue()
          const post =
            allValue[
              `${getArithmeticKey(key)}_percent_post` as keyof Database_Procs_TeamJTF_Item
            ] || 0
          const pre =
            allValue[
              `${getArithmeticKey(key)}_percent_pre` as keyof Database_Procs_TeamJTF_Item
            ] || 0
          let title = ''
          if (subKey === 'best') {
            if (best > 0) {
              // if there's a post score
              if (Number(post) > -1) {
                let text = `This player's Best score is the highest value he/she has scored while playing this game.`

                if (post < best) {
                  text += `Note:- that the Post score of ${post} is lower than the Best score of ${best}, which means that this player scored ${best} at one time, but then scored ${post} in his/her most recent attempt`
                }

                title = text
              }
              // no post score
              else {
                title =
                  Number(pre) < 80
                    ? `This player scored ${pre} on the pre-test. After that, his/her highest score was ${best}.`
                    : `This player scored ${pre} on the pre-test, which ${
                        pre === 80 ? 'meets' : 'is above'
                      } the proficiency level of 80, so he/she has been credited with ${pre} as a Best score.`
              }
            }
            // no best score
            else {
              title = `This player has no Best score yet. Note that scores on the pre-test below the proficiency level of 80 are not counted.`
            }
          } else {
            title = info.getValue()
              ? `This test was completed on ${formatReportDate(
                  new Date((allValue[dateKey] as string) || '')
                )}`
              : 'This test has not yet been completed'
          }
          return (
            <Tooltip
              title={title}
              classNames={{ root: 'progress-tooltip' }}
              placement="right"
              arrow={false}
            >
              {info.getValue() as string}
            </Tooltip>
          )
        },
        size: 120
      }
    })
  }))

  const actioncolumn = actionColumn<Database_Procs_TeamJTF_Item>(
    hidableSelectLabel,
    sendMessage
  )

  const commonCols =
    generateScrollableAssessmentCommonColumns<Database_Procs_TeamJTF_Item>(
      'jtf'
    )

  /**
   * handles table column mapping
   */
  const finalColumns: ColumnType<Database_Procs_TeamJTF_Item>[] = [
    actioncolumn,
    ...commonCols,
    ...jtfColumns
  ]

  return finalColumns
}
function schoolColumns() {
  const jtfColumns: ColumnType<Database_Procs_JTFAggregatedByLevel_Item>[] = [
    {
      header: () => renderHeaderTooltip('Team Name'),
      id: 'name',
      accessorKey: 'name',
      size: 100,
      cell: (info) => <b>{String(info.getValue() || 'None')}</b>
    },
    {
      header: () => renderHeaderTooltip('Class Name'),
      id: 'desc',
      accessorKey: 'desc',
      size: 100,
      cell: (info) => <b>{String(info.getValue() || 'None')}</b>
    },
    {
      header: () => renderHeaderTooltip('Teacher'),
      id: 'contactName',
      accessorKey: 'contactName',
      size: 100,
      cell: (info) => <b>{String(info.getValue() || 'None')}</b>
    },
    {
      header: () => renderHeaderTooltip('Active Students'),
      id: 'activeStudents',
      accessorKey: 'activeStudents',
      size: 100,
      cell: (info) => <b>{String(info.getValue() || 'None')}</b>
    },
    {
      header: 'Pre',
      id: 'pre',
      columns: [
        {
          header: () => renderHeaderTooltip('# Taking Test'),
          id: 'numberPretest',
          accessorKey: 'numberPretest',
          size: 70
        },
        {
          header: () => renderHeaderTooltip('# Proficient+'),
          id: 'numberFluentPretest',
          accessorKey: 'numberFluentPretest',
          size: 70,
          cell: (info: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const calculatedValue =
              info.row.original.numberFluentPretest +
              info.row.original.numberProficientPretest

            return calculatedValue >= 0 ? `${calculatedValue}` : '-'
          }
        },
        {
          header: () => renderHeaderTooltip('% Proficient+'),
          id: 'preProficient',
          accessorKey: 'preProficient',
          size: 70,
          cell: (info: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const calculatedValue =
              info.row.original.numberFluentPretest +
              info.row.original.numberProficientPretest
            const percent =
              (calculatedValue / info.row.original.numberPretest) * 100
            return percent >= 0 ? `${percent.toFixed(1)}%` : '-'
          }
        }
      ]
    },
    {
      header: 'Post',
      id: 'post',
      columns: [
        {
          header: () => renderHeaderTooltip('# Taking Test'),
          id: 'numberPosttest',
          accessorKey: 'numberPosttest',
          size: 70
        },
        {
          header: () => renderHeaderTooltip('# Proficient+'),
          id: 'numberFluent',
          accessorKey: 'numberFluent',
          size: 70,
          cell: (info: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const calculatedValue =
              info.row.original.numberFluent +
              info.row.original.numberProficient

            return calculatedValue >= 0 ? `${calculatedValue}` : '-'
          }
        },
        {
          header: () => renderHeaderTooltip('% Proficient+'),
          id: 'postProficient',
          accessorKey: 'postProficient',
          size: 70,
          cell: (info: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const calculatedValue =
              info.row.original.numberFluent +
              info.row.original.numberProficient
            const percent =
              (calculatedValue / info.row.original.numberPosttest) * 100
            return percent >= 0 ? `${percent.toFixed(1)}%` : '-'
          }
        }
      ]
    }
  ]
  return jtfColumns
}
export { schoolColumns, columns }
